import { Box, Divider, Icon } from "@outschool/backpack";
import { faCheck } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { useSession } from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import { Filter, FilterList } from "@outschool/ui-components-website";
import React from "react";

import { useSearchByLearner } from "../../../providers/SearchByLearnerProvider";
import AddLearnerModal from "../../parentDashboardV2/components/AddLearnerModal";
import { useUpsertLearner } from "../../parentDashboardV2/hooks/useUpsertLearner";

/**
 * Learner filter selector for desktop search.
 */
export default function LearnerFilters({
  hideTitle = false,
}: {
  hideTitle?: boolean;
}) {
  const { t } = useTranslation("client\\components\\search\\LearnerFilters");
  const { currentUser } = useSession();
  const { learner, setLearner } = useSearchByLearner();

  const learners = currentUser?.children || [];

  const handleLearnerSelect = (selectedLearnerUid: string | null) => {
    if (!selectedLearnerUid) {
      setLearner(null);
      return;
    }

    const selectedLearner = learners.find(
      child => child.uid === selectedLearnerUid,
    );

    if (selectedLearner) {
      setLearner(selectedLearner);
    }
  };
  const { openModal, upsertChild, ...addLearnerModalProps } =
    useUpsertLearner();

  const handleAddLearner = async () => {
    const newLearner = await upsertChild();
    if (newLearner) {
      setLearner(newLearner);
    }
    return newLearner;
  };

  const content = (
    <Box sx={{ display: "flex", flexDirection: "column", padding: 0 }}>
      {learners.length > 0 && (
        <>
          <TrackedButton
            variant="text"
            key="all-learners-button"
            id="all-learners"
            onClick={() => handleLearnerSelect(null)}
            sx={{
              width: "100%",
              justifyContent: "flex-start",
              borderRadius: 0,
              display: "flex",
              padding: "0.5em",
              color: learner === null ? "primary.main" : "text.primary",
              gap: "0.5em",
            }}
            data-test-id="learner-filter-button-option-all"
            trackingName="learner_filter_select"
            trackingSharedProperties={{
              learner_uid: "all",
              location: "desktop_search_dropdown",
            }}
          >
            {learner === null && <Icon icon={faCheck} />}
            {t("All learners")}
          </TrackedButton>
          {learners.map(child => (
            <TrackedButton
              variant="text"
              key={child.uid}
              id={child.uid}
              onClick={() => handleLearnerSelect(child.uid)}
              sx={{
                width: "100%",
                justifyContent: "flex-start",
                borderRadius: 0,
                display: "flex",
                padding: "0.5em",
                color:
                  learner?.uid === child.uid ? "primary.main" : "text.primary",
                gap: "0.5em",
              }}
              data-test-id={`learner-filter-button-option-${child.uid}`}
              trackingName="learner_filter_select"
              trackingSharedProperties={{
                learner_uid: child.uid,
                location: "desktop_search_dropdown",
              }}
            >
              {learner?.uid === child.uid && <Icon icon={faCheck} />}
              {child.name}
            </TrackedButton>
          ))}
          <Divider sx={{ margin: "0.5em" }} />
        </>
      )}
      <AddLearnerModal
        {...addLearnerModalProps}
        upsertChild={handleAddLearner}
      />
      <TrackedButton
        onClick={openModal}
        variant="text"
        key="add-learner-button"
        sx={{
          width: "100%",
          justifyContent: "flex-start",
          borderRadius: 0,
          display: "flex",
          padding: "0.5em",
        }}
        trackingName="learner_filter_add_learner_button"
        trackingSharedProperties={{ location: "desktop_search_dropdown" }}
      >
        {t("Add a learner")}
      </TrackedButton>
    </Box>
  );

  if (hideTitle) {
    return content;
  }

  return (
    <FilterList>
      <Filter title={t("Select learner")}>{content}</Filter>
    </FilterList>
  );
}
